import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import type { Locale } from '@diana/shared'
import { api, type StudentPlan } from '../../lib/api.js'
import { RowText, Rows, SectionHead, Status, type Tone } from '../../classroom/ui.js'
import { titleFor } from './CoursePage.js'
import { shellCopy } from './copyShell.js'

/**
 * „Nasz plan” (Lekcje z Dianą): the lessons Diana has released from the
 * courses for the classes she teaches this student, in her order, with the
 * goal and her note on each. A row opens the lesson in the player, like any
 * course lesson. The first one not yet finished is marked as next.
 *
 * Nothing shows for a student without a plan.
 */
export function OurPlan({ locale, timezone }: { locale: Locale; timezone: string }) {
  const { data } = useQuery({ queryKey: ['course', 'plans'], queryFn: api.course.plans })
  if (!data || data.length === 0) return null
  return (
    <>
      {data.map((plan) => (
        <PlanBlock key={plan.id} plan={plan} several={data.length > 1} locale={locale} timezone={timezone} />
      ))}
    </>
  )
}

function PlanBlock({ plan, several, locale, timezone }: { plan: StudentPlan; several: boolean; locale: Locale; timezone: string }) {
  const s = shellCopy(locale)
  const p = s.plan
  const nextId = plan.items.find((i) => i.completed < i.lessons)?.id ?? null
  // With several plans the goal names each one in its heading; with one it reads under it.
  const meta = [!several && plan.goal ? `${p.goal}: ${plan.goal}` : '', plan.with.length ? p.with(plan.with.join(', ')) : '']
    .filter(Boolean)
    .join(' · ')

  return (
    <section className="flex flex-col">
      <SectionHead>{several && plan.goal ? `${p.title} · ${plan.goal}` : p.title}</SectionHead>
      {meta && <p className="m-0 -mt-1 pb-3 text-class-ink-soft">{meta}</p>}
      {plan.items.length === 0 ? (
        <p className="m-0 border-t border-class-line py-4 text-class-gloss text-class-ink-soft">{p.empty}</p>
      ) : (
        <Rows>
          {plan.items.map((item) => {
            const whole = !item.lesson
            const target = item.lesson ?? item.continueAt
            const to = target ? `/panel/kurs/${item.course.slug}/${target.slug}` : `/panel/kurs/${item.course.slug}`
            const title = item.lesson ? `${s.course.lesson(item.lesson.number)} · ${titleFor(item.lesson, locale)}` : `${p.wholeCourse}: ${item.course.title}`

            const done = item.completed >= item.lessons && item.lessons > 0
            const [tone, word]: [Tone, string] = whole
              ? [done ? 'ok' : 'info', p.lessonsOf(item.completed, item.lessons)]
              : done
                ? ['ok', p.done]
                : item.id === nextId
                  ? ['info', p.next]
                  : item.started
                    ? ['wait', p.started]
                    : ['neutral', p.fresh]

            const day = item.classAt ? DateTime.fromISO(item.classAt).setZone(timezone).setLocale(locale).toFormat('ccc d.MM') : ''
            const line = [
              item.lesson ? item.course.title : '',
              day ? p.forClass(day) : '',
              item.homework ? p.homework(item.homework.done, item.homework.parts) : '',
              whole && item.continueAt && !done ? p.continueAt(item.continueAt.number) : '',
            ]
              .filter(Boolean)
              .join(' · ')

            return (
              <Link key={item.id} to={to} className="flex min-h-16 items-center gap-4 px-1 py-3 !text-class-ink hover:bg-class-field-soft">
                <span className="min-w-0 flex-1">
                  <RowText title={<span lang={locale === 'pl' || !item.lesson ? 'pl' : locale}>{title}</span>} meta={line || undefined} />
                  {item.note && (
                    <span lang="pl" className="mt-1 block text-class-gloss text-class-ink-soft">
                      „{item.note}”
                    </span>
                  )}
                </span>
                <Status tone={tone} className="shrink-0 !text-class-gloss">
                  {word}
                </Status>
                <span aria-hidden className="text-class-ink-faint">
                  ›
                </span>
              </Link>
            )
          })}
        </Rows>
      )}
    </section>
  )
}
